import { axiosInstance } from "@/lib/axios";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { getAuthToken, getLoginType } from "@/utils/secureStorage";

export interface Employee {
  _id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  role: "employee" | "department_admin" | "company_admin";
  companyId: string;
  departmentId: string;
  department?: {
    _id: string;
    name: string;
  };
  photo: string;
  isVerified: boolean;
  isActive: boolean;
  lastLogin: string;
  createdAt: string;
  updatedAt: string;
}

interface UsersResponse {
  status: string;
  data: {
    documents: Employee[];
    total: number;
    page: number;
    limit: number;
  };
}

export const useCompanyEmployees = (
  companyId: string,
  options?: Partial<UseQueryOptions<UsersResponse>>
) => {
  return useQuery<UsersResponse>({
    queryKey: ["companyEmployees", companyId],
    queryFn: async () => {
      // Check if we have a valid auth token before making the request
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Check login type to determine if this is a company admin
      const loginType = getLoginType();
      console.log("Fetching company employees with login type:", loginType);

      try {
        // Try company-specific endpoint first for company admins
        if (loginType === "company") {
          console.log("Trying company-specific employees endpoint");
          const { data } = await axiosInstance.get(
            `/api/v1/companies/${companyId}/employees`
          );
          return data;
        }
      } catch (companyError: any) {
        console.log(
          "Company-specific endpoint failed, falling back to users endpoint:",
          companyError.response?.status
        );
        // If company endpoint fails, continue to users endpoint
      }

      // Fall back to users endpoint
      console.log("Using users endpoint for employees");
      const { data } = await axiosInstance.get(
        `/api/v1/users?company=${companyId}&role=employee&role=department_admin&role=company_admin`
      );
      return data;
    },
    enabled: !!companyId && !!getAuthToken(),
    retry: (failureCount, error: any) => {
      // Don't retry on 401 errors
      if (error?.response?.status === 401) {
        console.log("401 error in useCompanyEmployees, not retrying");
        return false;
      }
      return failureCount < 1;
    },
    retryDelay: 1000,
    // Use global defaults for refetching
    ...options,
  });
};

export const useUser = (userId: string) => {
  return useQuery({
    queryKey: ["user", userId],
    queryFn: async () => {
      // Check if we have a valid auth token before making the request
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const { data } = await axiosInstance.get<{ data: Employee }>(
        `/api/v1/users/${userId}`
      );
      return data.data;
    },
    enabled: !!userId && !!getAuthToken(),
    retry: (failureCount, error: any) => {
      // Don't retry on 401 errors
      if (error?.response?.status === 401) {
        return false;
      }
      return failureCount < 1;
    },
  });
};

export const useUserRanking = () => {
  return useQuery({
    queryKey: ["user-ranking"],
    queryFn: async () => {
      try {
        // Check authentication
        const token = getAuthToken();
        const loginType = getLoginType();
        console.log("🔍 Ranking API - Auth check:", {
          hasToken: !!token,
          loginType,
          tokenPreview: token ? `${token.substring(0, 20)}...` : "none",
        });

        // Use the exact endpoint from backend
        const endpoint = "/api/v1/users/get-my-ranking";
        console.log(`🔍 Fetching user ranking from: ${endpoint}`);

        const response = await axiosInstance.get(endpoint);
        console.log("🔍 Ranking API response:", response.data);
        return response.data;
      } catch (error: any) {
        console.error("❌ Failed to fetch user ranking:", error);
        console.error("❌ Error details:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
        });
        // Return null instead of throwing to prevent component crashes
        return null;
      }
    },
    // Use global defaults for staleTime
    retry: 2,
    // Don't retry on 401/403 errors (user not authorized)
    retryOnMount: false,
    enabled: true, // Always enable the query
  });
};
